import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { WallpaperQRCode } from './WallpaperQRCode';
import { WallpapersHistory } from './WallpapersHistory';
import { useQRCode } from '../hooks/useQRCode';
import { useWallpapersContext } from '../contexts/WallpapersContext';
import { useTheme } from '../contexts/ThemeContext';
import { createRoot } from 'react-dom/client';
import { Upload, Download, QrCode, Image as ImageIcon, History } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { Button } from '../components/ui/Button';
// import { useToast } from '../components/ui/use-toast';
// import { Card } from '../components/ui/card';

interface QRCodeExportProps {
  value: string;
  name: string;
  logoUrl?: string;
  wallpaperUrl?: string;
  onLogoUpload: (file: File) => Promise<void>;
  onQRCodeSave: (blob: Blob) => Promise<string>;
  qrStyle: {
    fgColor: string;
    bgColor: string;
    style: 'dots' | 'squares' | 'rounded';
  };
  onStyleChange: (style: any) => void;
}

export function QRCodeExport({
  value,
  name,
  logoUrl,
  // wallpaperUrl,
  onLogoUpload,
  onQRCodeSave,
  qrStyle,
  onStyleChange,
}: QRCodeExportProps) {
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeColor, setActiveColor] = useState<'fg' | 'bg' | null>(null);
  const [activeTab, setActiveTab] = useState<'qr' | 'wallpaper' | 'history'>('qr');
  const { qrCodeData, updateQRCode, hasQRCode } = useQRCode();
  const { saveWallpaper } = useWallpapersContext();
  const { setPrimaryColor } = useTheme();

  const [prevValue, setPrevValue] = useState(value);
  const [prevLogoUrl, setPrevLogoUrl] = useState(logoUrl);
  const [prevQrStyle, setPrevQrStyle] = useState(qrStyle);

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await onLogoUpload(file);
    }
  };

  const QRCodeWithLogo = () => {
    return (
      <div className="relative" ref={qrCodeRef}>
        <QRCodeSVG
          value={value}
          size={256}
          level="H"
          fgColor={qrStyle.fgColor}
          bgColor={qrStyle.bgColor}
        />
        {logoUrl && (
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{ pointerEvents: 'none' }}
          >
            <div 
              className="bg-white p-1 rounded-sm"
              style={{ width: '20%', height: '20%' }}
            >
              <img
                src={logoUrl}
                alt="Logo"
                className="w-full h-full object-contain"
                crossOrigin="anonymous"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const createQRCodeImage = async (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      try {
        const canvas = document.createElement('canvas');
        const size = 1024;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Dessiner le QR code
        const qrImage = new Image();
        qrImage.crossOrigin = 'anonymous';

        qrImage.onload = () => {
          // Dessiner le QR code
          ctx.fillStyle = qrStyle.bgColor;
          ctx.fillRect(0, 0, size, size);
          ctx.drawImage(qrImage, 0, 0, size, size);

          // Si un logo est présent, le dessiner au centre
          if (logoUrl) {
            const logoImage = new Image();
            logoImage.crossOrigin = 'anonymous';
            logoImage.src = logoUrl;

            logoImage.onload = () => {
              // Calculer la taille et la position du logo
              const logoSize = size * 0.2; // 20% de la taille du QR code
              const logoX = (size - logoSize) / 2;
              const logoY = (size - logoSize) / 2;

              // Dessiner un fond blanc pour le logo
              ctx.fillStyle = 'white';
              ctx.fillRect(logoX, logoY, logoSize, logoSize);

              // Dessiner le logo
              ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);

              // Convertir le canvas en blob
              canvas.toBlob((blob) => {
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error('Could not convert canvas to blob'));
                }
              }, 'image/png');
            };

            logoImage.onerror = () => {
              // En cas d'erreur avec le logo, on sauvegarde quand même le QR code sans logo
              canvas.toBlob((blob) => {
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error('Could not convert canvas to blob'));
                }
              }, 'image/png');
            };
          } else {
            // S'il n'y a pas de logo, convertir directement le canvas en blob
            canvas.toBlob((blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Could not convert canvas to blob'));
              }
            }, 'image/png');
          }
        };

        qrImage.onerror = () => {
          reject(new Error('Could not load QR code image'));
        };

        // Créer le QR code en SVG
        const svgElement = qrCodeRef.current?.querySelector('svg');
        if (!svgElement) {
          reject(new Error('Could not find QR code SVG element'));
          return;
        }

        const svgString = new XMLSerializer().serializeToString(svgElement);
        const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        qrImage.src = URL.createObjectURL(blob);

      } catch (error) {
        reject(error);
      }
    });
  };

  useEffect(() => {
    console.log('useEffect déclenché avec les valeurs:', {
      value,
      logoUrl,
      qrStyle,
      prevValue,
      prevLogoUrl,
      prevQrStyle
    });

    const hasStyleChanged = 
      qrStyle.fgColor !== prevQrStyle.fgColor || 
      qrStyle.bgColor !== prevQrStyle.bgColor || 
      qrStyle.style !== prevQrStyle.style;

    const hasChanged = 
      value !== prevValue || 
      logoUrl !== prevLogoUrl || 
      hasStyleChanged;

    console.log('Changements détectés:', hasChanged, {
      styleChanged: hasStyleChanged,
      valueChanged: value !== prevValue,
      logoChanged: logoUrl !== prevLogoUrl,
      currentStyle: qrStyle,
      prevStyle: prevQrStyle
    });

    if (hasChanged) {
      const timeoutId = setTimeout(async () => {
        try {
          // Obtenir directement le Blob du QR code
          const qrCodeBlob = await createQRCodeImage();
          // Sauvegarder le QR code dans le storage
          const qrCodeUrl = await onQRCodeSave(qrCodeBlob);
          
          if (qrCodeUrl) {
            updateQRCode(qrCodeUrl);
            
            // Mettre à jour les valeurs précédentes seulement après une sauvegarde réussie
            setPrevValue(value);
            setPrevLogoUrl(logoUrl);
            setPrevQrStyle({...qrStyle});
          }
        } catch (error) {
          console.error('Erreur lors de la sauvegarde du QR code:', error);
        }
      }, 1000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [value, logoUrl, qrStyle, prevValue, prevLogoUrl, prevQrStyle, onQRCodeSave, updateQRCode]);

  useEffect(() => {
    if (qrStyle.fgColor) {
      setPrimaryColor(qrStyle.fgColor);
    }
  }, [qrStyle.fgColor, setPrimaryColor]);

  const downloadQRCode = async () => {
    if (!qrCodeRef.current) return;

    try {
      // Obtenir directement le Blob du QR code
      const qrCodeBlob = await createQRCodeImage();
      const link = document.createElement('a');
      link.download = `${name}_qr.png`;
      link.href = URL.createObjectURL(qrCodeBlob);
      link.click();
    } catch (error) {
      console.error('Erreur lors du téléchargement du QR code:', error);
    }
  };

  const SubTabButton = ({ isActive, icon, label, onClick }: { isActive: boolean; icon: React.ReactNode; label: string; onClick: () => void }) => {
    return (
      <button
        onClick={onClick}
        className={`relative flex flex-col items-center justify-center p-4 group ${
          isActive ? 'text-primary-600' : 'text-gray-500 hover:text-primary-600'
        }`}
      >
        <div className="relative">
          {icon}
          <span className="hidden md:block absolute left-1/2 transform -translate-x-1/2 -bottom-6 px-2 py-1 text-sm whitespace-nowrap
            bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {label}
          </span>
        </div>
      </button>
    );
  };

  const tabs = [
    { id: 'qr', icon: <QrCode className="w-6 h-6" />, label: "QR Code Simple" },
    { id: 'wallpaper', icon: <ImageIcon className="w-6 h-6" />, label: "Fond d'écran" },
    { id: 'history', icon: <History className="w-6 h-6" />, label: "Historique" },
  ];

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 mb-8">
        <nav className="flex justify-center space-x-4 md:space-x-8" aria-label="Sous-onglets">
          {tabs.map((tab) => (
            <SubTabButton
              key={tab.id}
              isActive={activeTab === tab.id}
              icon={tab.icon}
              label={tab.label}
              onClick={() => setActiveTab(tab.id)}
            />
          ))}
        </nav>
      </div>

      {activeTab === 'qr' && (
        <div className="space-y-6">
          <div className="flex flex-col gap-4 items-center justify-center">
            <QRCodeWithLogo />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Logo</label>
              <div className="mt-1 flex items-center space-x-4">
                {logoUrl && (
                  <img src={logoUrl} alt="Logo" className="h-12 w-12 object-contain" />
                )}
                <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  <Upload className="h-4 w-4 mr-2" />
                  Choisir un logo
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Style</label>
              <div className="mt-1 grid grid-cols-2 gap-4">
                <div>
                  <button
                    onClick={() => {
                      setShowColorPicker(true);
                      setActiveColor('fg');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 flex items-center"
                  >
                    <div 
                      className="w-4 h-4 mr-2 rounded-sm border border-gray-300" 
                      style={{ backgroundColor: qrStyle.fgColor }}
                    />
                    Couleur QR
                  </button>
                </div>
                <div>
                  <button
                    onClick={() => {
                      setShowColorPicker(true);
                      setActiveColor('bg');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 flex items-center"
                  >
                    <div 
                      className="w-4 h-4 mr-2 rounded-sm border border-gray-300" 
                      style={{ backgroundColor: qrStyle.bgColor }}
                    />
                    Couleur fond
                  </button>
                </div>
              </div>
            </div>
          </div>

          {showColorPicker && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-4 rounded-lg">
                <div className="space-y-4">
                  <HexColorPicker
                    color={activeColor === 'fg' ? qrStyle.fgColor : qrStyle.bgColor}
                    onChange={(color) => {
                      if (activeColor) {
                        onStyleChange({
                          ...qrStyle,
                          [activeColor === 'fg' ? 'fgColor' : 'bgColor']: color,
                        });
                      }
                    }}
                  />
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={activeColor === 'fg' ? qrStyle.fgColor : qrStyle.bgColor}
                      onChange={(e) => {
                        const color = e.target.value;
                        // Vérifier si la couleur est au format hexadécimal valide
                        if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
                          if (activeColor) {
                            onStyleChange({
                              ...qrStyle,
                              [activeColor === 'fg' ? 'fgColor' : 'bgColor']: color,
                            });
                          }
                        }
                      }}
                      placeholder="#000000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm"
                    />
                  </div>
                  <button
                    onClick={() => setShowColorPicker(false)}
                    className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <Button
              onClick={downloadQRCode}
              icon={Download}
              variant="primary"
              className="bg-primary-600 text-white hover:bg-primary-700"
            >
              Télécharger
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'wallpaper' && (
        <WallpaperQRCode
          savedQRCode={qrCodeData}
          hasQRCode={hasQRCode}
          uploadWallpaperBlob={saveWallpaper}
        />
      )}

      {activeTab === 'history' && (
        <WallpapersHistory />
      )}
    </div>
  );
}